/* eslint-disable no-restricted-imports */
import { ThingEventSnapshot } from '@eagle/core-data-types';
import { TrackingEventTypes } from '@eagle/data-function-types';
import { ArrowUp, Flag } from '@ethings/icons';
import { Theme } from '@mui/material';
import L from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { EventDefinition } from '../../../../notable-events';
import { makeStyles } from '../../../../theme';
import { Maybe } from '../../../../types';
import { getEventDictionary } from '../../../entity-journey';
import { EventLocationData } from '../../thing-event-pane/thing-event-pane.types';

export const POINT_RADIUS = 15.0;
export const ENLARGED_POINT_RADIUS = POINT_RADIUS * 1.2;
export const START_END_CLUSTER_TOLERANCE = 10;
export const SPIDERFY_DISTANCE_MULTIPLIER = 1.5;
export const SPIDERFY_SEPARATION = 28.0;
export const SPIDERFY_LENGTH_FACTOR = 5.0;
export const SPIDERFY_INITIAL_LENGTH = SPIDERFY_DISTANCE_MULTIPLIER * 11.0;
export const SPIDERFY_ANGLE_OFFSET = 0.0005;
const START_END_SCALE = 1.2;

interface NotableEventDataItem {
  enabled: boolean;
  eventData: EventLocationData;
  index: number;
  priority: number;
}

interface NotableEvents {
  containsFirstEvent: boolean;
  containsLastEvent: boolean;
  notableEventData: NotableEventDataItem[];
}

export const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

export const getMarkerSize = (eventId: string, hoveredEventId: Maybe<string>, expanded: boolean): number => {
  if (!expanded) return POINT_RADIUS;
  return eventId === hoveredEventId ? ENLARGED_POINT_RADIUS : POINT_RADIUS;
};

export const getClusterMarkerSize = (eventIds: string[], hoveredEventId: Maybe<string>, expanded: boolean, defaultRadius?: number): number => {
  const clusterRadius = defaultRadius ?? POINT_RADIUS;
  if (!expanded && hoveredEventId && eventIds.includes(hoveredEventId)) return clusterRadius * START_END_SCALE;
  if (!expanded) return clusterRadius;
  return eventIds[0] === hoveredEventId ? clusterRadius * START_END_SCALE : clusterRadius;
};

export const getNotableEventColor = (event: ThingEventSnapshot, theme: Theme, notableEvents: EventDefinition[]): string => {
  const eventData = getEventDictionary(event, theme, notableEvents) ?? notableEvents[0];
  return eventData.styling.backgroundColor;
};

export const getNotableEventsInformation = (
  items: {
    eventData: EventLocationData;
    index: number;
  }[], eventsLength: number, notableEvents: EventDefinition[],
): NotableEvents => {
  const resolveDefinition = (item: { eventData: EventLocationData }): EventDefinition | undefined => {
    const exactMatch = notableEvents.find((event) => (
      event.featureId === item.eventData.featureTypeId
      && event.eventTypeId === item.eventData.eventTypeId
    ));

    const featureWildcardMatch = notableEvents.find((event) => (
      event.featureId === '*'
      && event.eventTypeId === item.eventData.eventTypeId
    ));

    const eventTypeWildcardMatch = notableEvents.find((event) => (
      event.featureId === item.eventData.featureTypeId
      && event.eventTypeId === '*'
    ));

    const defaultMatch = notableEvents.find((event) => event.featureId === '*' && event.eventTypeId === '*');
    return exactMatch ?? featureWildcardMatch ?? eventTypeWildcardMatch ?? defaultMatch;
  };

  const locationUpdateEvents = items.filter((item) =>
    item.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE,
  );

  const updatedEvents = [
    ...items.filter((item) => item.eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE),
    ...locationUpdateEvents,
  ];

  const notableEventData = updatedEvents.map((item) => {
    const dictionarySearch = resolveDefinition(item);
    const isLocationUpdate = item.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE;

    // Keep non-location events visible on the map by default; location updates still honor configured visibility.
    const enabled = isLocationUpdate ? Boolean(dictionarySearch?.enabled) : true;

    return {
      ...item,
      enabled,
      priority: dictionarySearch?.priority ?? Number.MAX_SAFE_INTEGER,
    };
  }).filter((item) => item.enabled);

  const containsFirstEvent = !!items.find((eventItem) => eventItem.index === 0);
  const containsLastEvent = !!items.find((eventItem) => eventItem.index === (eventsLength - 1));

  return {
    containsFirstEvent,
    containsLastEvent,
    notableEventData,
  };
};

export const createClusterMarker = (
  color: string,
  size: number,
  firstMarker: boolean,
  lastMarker: boolean,
  isOngoing: boolean,
  iconColor: string,
  locationUpdate: boolean,
  theme: Theme,
  heading?: number,
): L.DivIcon => {
  const overwriteIconColor = !locationUpdate ? color : iconColor;

  const styles = makeStyles()(() => ({
    icon: {
      backgroundColor: (firstMarker || lastMarker) ? overwriteIconColor : color,
      borderRadius: '50%',
      boxShadow: theme.shadows[4],
      '& .MuiSvgIcon-root': {
        height: '100%',
        width: '100%',
      },
      zIndex: 1000,
    },
  }))();

  if (firstMarker) {
    return L.divIcon({
      className: '',
      html: renderToStaticMarkup(
        <div
          style={{
            alignItems: 'center',
            backgroundColor: iconColor,
            borderRadius: '50%',
            boxShadow: theme.shadows[4],
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <ArrowUp
            data-testid="arrow-up-icon"
            style={{
              color: theme.palette.common.white,
              height: '100%',
              transform: `rotate(${heading ?? 0}deg)`,
              width: '100%',
              padding: '2px',
            }}
          />
        </div>,
      ),
      iconSize: [size * START_END_SCALE, size * START_END_SCALE],
    });
  }

  if (lastMarker) {
    if (isOngoing) {
      const pulseColor = theme.palette.success.light;
      const pulseShadowSize = 10;
      return L.divIcon({
        className: '',
        html: [
          '<div style="position:relative;width:16px;height:16px;">',
          '<style>@keyframes trip-history-ongoing-pulse{0%{transform:scale(0.9);box-shadow:0 0 0 0 ',
          pulseColor,
          ';}70%{transform:scale(1);box-shadow:0 0 0 ',
          pulseShadowSize,
          'px ',
          `${pulseColor}00`,
          ';}100%{transform:scale(0.9);box-shadow:0 0 0 0 ',
          `${pulseColor}00`,
          ';}}</style>',
          '<div style="width:16px;height:16px;border-radius:50%;background:',
          pulseColor,
          ';box-shadow:0 0 0 0 ',
          pulseColor,
          ';animation:trip-history-ongoing-pulse 2000ms infinite;"></div>',
          '</div>',
        ].join(''),
        iconSize: [16, 16],
      });
    }

    return L.divIcon({
      className: styles.classes.icon,
      html: renderToStaticMarkup(<Flag data-testid="flag-icon" style={{ color: theme.palette.common.white, padding: '2px' }} />),
      iconSize: [size * START_END_SCALE, size * START_END_SCALE],
    });
  }

  return L.divIcon({ className: styles.classes.icon, iconSize: [size, size] });
};

export const createBoundingMarker = (size: number): L.DivIcon => {
  const styles = makeStyles()(() => ({
    icon: {
      backgroundColor: 'transparent',
      borderRadius: '50%',
    },
  }))();

  return L.divIcon({ className: styles.classes.icon, iconSize: [size, size] });
};
